import { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faArrowTrendUp,
  faBell,
  faBriefcase,
  faCalendarCirclePlus,
  faCalendarDays,
  faCheck,
  faChevronDown,
  faClock,
  faEllipsis,
  faImages,
  faMagnifyingGlass,
  faMessages,
  faTableCellsLarge,
  faUser,
  faWallet,
} from "@fortawesome/pro-solid-svg-icons";

/**
 * HTML/CSS recreation of the two app dashboards (creator + member) from the
 * approved App Store screens — no raster device images. Every block carries a
 * `gta-fly` reveal with a staggered delay, so when the billboard scrolls into
 * view the phones rise in and their UI elements float into the frames.
 * Chips are the glass in-app notifications, rebuilt in HTML.
 *
 * Colors are the app's own dark ramp (#000 → #101010 → #1A1A1A) with brand
 * cyan #44C2E3 / lime #C0FF00 exactly as they appear in product UI (lime as
 * control fill / stat tint per the real dashboards).
 */

const CYAN = "#44C2E3";
const LIME = "#C0FF00";

const CSS = `
.gta-devices { --phone-fy: 110px; }
@media (max-width: 1023px) {
  /* Mobile: phones start below the card's bottom edge and rise into place. */
  .gta-devices { --phone-fy: 380px; }
}
.gta-fly {
  opacity: 0;
  transform: translate3d(var(--fx, 0px), var(--fy, 40px), 0) rotate(var(--r, 0deg));
  transition: opacity 0.9s ease, transform 0.9s cubic-bezier(0.22, 1, 0.36, 1);
  transition-delay: var(--d, 0s);
  will-change: transform, opacity;
}
.gta-in .gta-fly {
  opacity: 1;
  transform: translate3d(0, 0, 0) rotate(var(--r, 0deg));
}
@media (prefers-reduced-motion: no-preference) {
  .gta-chip-drift { animation: gtaChipDrift 6s ease-in-out 2.2s infinite alternate; }
  @keyframes gtaChipDrift { from { transform: translateY(0); } to { transform: translateY(-8px); } }
}
@media (prefers-reduced-motion: reduce) {
  .gta-fly { opacity: 1; transition: none; transform: rotate(var(--r, 0deg)); }
}
`;

/** Reveal-once observer — flips the container class when scrolled into view. */
const useInViewOnce = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { threshold: 0.25 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return { ref, inView };
};

/* ---------- shared micro-parts ---------- */

const Fly = ({
  d,
  fy = 26,
  fx = 0,
  r = 0,
  className = "",
  children,
}: {
  d: number;
  /** Pixels, or a CSS length/var() string (e.g. "var(--phone-fy)"). */
  fy?: number | string;
  fx?: number;
  r?: number;
  className?: string;
  children: React.ReactNode;
}) => (
  <div
    className={`gta-fly ${className}`}
    style={
      {
        "--d": `${d}s`,
        "--fy": typeof fy === "number" ? `${fy}px` : fy,
        "--fx": `${fx}px`,
        "--r": `${r}deg`,
      } as React.CSSProperties
    }
  >
    {children}
  </div>
);

const Label = ({ children }: { children: React.ReactNode }) => (
  <span className="block text-[8px] font-semibold uppercase tracking-[0.18em] text-zinc-500">
    {children}
  </span>
);

const LimePill = ({ children }: { children: React.ReactNode }) => (
  <span
    className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-2 text-[11px] font-bold text-black"
    style={{ backgroundColor: LIME }}
  >
    {children}
    <FontAwesomeIcon icon={faArrowRight} className="h-2.5 w-2.5" />
  </span>
);

const OutlinePill = ({ children, hasArrow = true }: { children: React.ReactNode; hasArrow?: boolean }) => (
  <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-white/40 px-3.5 py-2 text-[11px] font-semibold text-white">
    {children}
    {hasArrow && <FontAwesomeIcon icon={faArrowRight} className="h-2.5 w-2.5" />}
  </span>
);

const StatusBar = () => (
  <div className="flex items-center justify-between px-6 pt-3.5 pb-2">
    <span className="text-[11px] font-bold text-white">5:05</span>
    <span className="flex items-center gap-1.5">
      <span className="flex items-end gap-[2px]">
        {[3, 5, 7, 9].map((h) => (
          <span key={h} className="w-[2px] rounded-sm bg-white/80" style={{ height: h }} />
        ))}
      </span>
      <span className="h-[9px] w-[16px] rounded-[3px] border border-white/70 p-[1px]">
        <span className="block h-full w-3/4 rounded-[1px] bg-white/90" />
      </span>
    </span>
  </div>
);

const PhoneHeader = ({
  initials,
  name,
  d,
  rightIcons,
}: {
  initials: string;
  name: string;
  d: number;
  rightIcons: React.ReactNode;
}) => (
  <Fly d={d} className="flex items-center justify-between px-5 pt-2">
    <span className="flex items-center gap-2.5">
      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-zinc-800 text-[11px] font-bold text-white">
        {initials}
      </span>
      <span>
        <span className="block text-[9px] text-zinc-400">Welcome back</span>
        <span className="flex items-center gap-1 text-[13px] font-bold leading-tight text-white">
          {name}
          <FontAwesomeIcon icon={faChevronDown} className="h-2 w-2 text-zinc-400" />
        </span>
      </span>
    </span>
    <span className="flex items-center gap-3 text-zinc-300">{rightIcons}</span>
  </Fly>
);

const TabBar = ({
  tabs,
  d,
}: {
  tabs: { icon: typeof faUser; label: string; isActive?: boolean; badge?: string }[];
  d: number;
}) => (
  <Fly d={d} className="absolute inset-x-3 bottom-3">
    <div className="flex items-center justify-between rounded-2xl bg-[#101010]/95 px-4 py-2.5 backdrop-blur">
      {tabs.map((t) => (
        <span
          key={t.label}
          className="relative flex flex-col items-center gap-0.5"
          style={{ color: t.isActive ? CYAN : "#8A8A8A" }}
        >
          <FontAwesomeIcon icon={t.icon} className="h-3.5 w-3.5" />
          {t.badge && (
            <span className="absolute -right-1.5 -top-1 flex h-3 w-3 items-center justify-center rounded-full bg-red-500 text-[7px] font-bold text-white">
              {t.badge}
            </span>
          )}
          <span className="text-[7px] font-semibold">{t.label}</span>
        </span>
      ))}
    </div>
  </Fly>
);

/**
 * iPhone 17 Pro proportions: 402x874 logical points (6.3"), thin uniform
 * bezel, flat titanium edge, Dynamic Island. The frame is aspect-locked so
 * every phone renders at true 17 Pro geometry regardless of width.
 */
const PhoneFrame = ({
  children,
  className = "",
  d,
  r,
}: {
  children: React.ReactNode;
  className?: string;
  d: number;
  r: number;
}) => (
  <Fly d={d} fy="var(--phone-fy, 110px)" r={r} className={`absolute ${className}`}>
    <div className="rounded-[3.1rem] bg-gradient-to-b from-zinc-600 via-zinc-800 to-zinc-900 p-[3px] shadow-[0_-16px_80px_rgba(0,0,0,0.65)]">
      <div className="rounded-[2.9rem] bg-black p-[6px]">
        <div className="relative aspect-[402/874] w-full overflow-hidden rounded-[2.55rem] bg-black ring-1 ring-white/[0.06]">
          {/* Dynamic Island */}
          <div
            aria-hidden="true"
            className="absolute left-1/2 top-2.5 z-30 h-[24px] w-[84px] -translate-x-1/2 rounded-full bg-black ring-1 ring-white/[0.09]"
          />
          {children}
        </div>
      </div>
    </div>
  </Fly>
);

/* ---------- creator dashboard ---------- */

const EarningsChart = () => (
  <svg viewBox="0 0 260 52" className="mt-1 h-12 w-full" aria-hidden="true">
    <defs>
      <linearGradient id="gtaChartFill" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={CYAN} stopOpacity="0.35" />
        <stop offset="100%" stopColor={CYAN} stopOpacity="0" />
      </linearGradient>
    </defs>
    <path
      d="M0 46 C40 42 70 38 105 32 C150 24 200 16 260 6 L260 52 L0 52 Z"
      fill="url(#gtaChartFill)"
    />
    <path
      d="M0 46 C40 42 70 38 105 32 C150 24 200 16 260 6"
      fill="none"
      stroke={CYAN}
      strokeWidth="1.6"
    />
  </svg>
);

const CreatorPhone = () => (
  <PhoneFrame className="right-0 top-[8%] w-[268px] sm:w-[288px]" d={0.15} r={3}>
    <div className="h-full">
      <StatusBar />
      <PhoneHeader
        initials="EB"
        name="Eric Bowden"
        d={0.5}
        rightIcons={
          <>
            <span className="relative">
              <FontAwesomeIcon icon={faBell} className="h-3.5 w-3.5" />
              <span className="absolute -right-1.5 -top-1.5 flex h-3 w-3 items-center justify-center rounded-full bg-red-500 text-[7px] font-bold text-white">
                1
              </span>
            </span>
            <FontAwesomeIcon icon={faMagnifyingGlass} className="h-3.5 w-3.5" />
          </>
        }
      />
      <div className="px-5 pt-4">
        <Fly d={0.62}>
          <Label>Lifetime Earnings</Label>
          <span className="mt-1 block text-[27px] font-bold leading-none text-white">
            $53,084.00
          </span>
          <span className="mt-1.5 flex items-center gap-1 text-[10px] font-bold" style={{ color: LIME }}>
            <FontAwesomeIcon icon={faArrowTrendUp} className="h-2.5 w-2.5" />
            $14,915 in 2026
          </span>
        </Fly>
        <Fly d={0.74}>
          <EarningsChart />
        </Fly>
        <Fly d={0.86} className="mt-2 flex gap-2">
          <LimePill>View Wallet</LimePill>
          <OutlinePill>View Jobs</OutlinePill>
        </Fly>
        <div className="mt-4 flex gap-2">
          <Fly d={0.98} className="min-w-0 flex-1">
            <div className="rounded-xl bg-[#101010] p-3">
              <Label>Pipeline</Label>
              <span className="mt-1 block text-[19px] font-bold text-white">$3,058</span>
              <span className="mt-0.5 block text-[8px] leading-snug text-zinc-500">
                Locked-in from upcoming jobs
              </span>
            </div>
          </Fly>
          <Fly d={1.06} className="min-w-0 flex-1">
            <div className="rounded-xl bg-[#101010] p-3">
              <Label>Active Jobs</Label>
              <span className="mt-1 block text-[19px] font-bold text-white">12</span>
              <span className="mt-0.5 block text-[8px] leading-snug text-zinc-500">
                Confirmed &amp; in progress
              </span>
            </div>
          </Fly>
        </div>
        <Fly d={1.18} className="mt-3">
          <div className="rounded-2xl bg-[#101010] p-3.5">
            <div className="flex items-center justify-between">
              <Label>Next Job · Tomorrow</Label>
              <span
                className="rounded-full px-2 py-0.5 text-[8px] font-bold"
                style={{ color: CYAN, backgroundColor: "rgba(68,194,227,0.12)" }}
              >
                Upcoming
              </span>
            </div>
            <span className="mt-1.5 block text-[15px] font-bold text-white">Intro and Outtro</span>
            <span className="block text-[9px] text-zinc-400">Driven · Linthicum Heights, MD</span>
            <div className="mt-2.5 flex justify-between gap-2">
              <span>
                <Label>When</Label>
                <span className="text-[11px] font-bold text-white">Jul 16</span>
              </span>
              <span>
                <Label>Coverage</Label>
                <span className="text-[11px] font-bold text-white">4 hrs</span>
              </span>
              <span>
                <Label>Est. Payout</Label>
                <span className="text-[11px] font-bold" style={{ color: CYAN }}>
                  $115.40
                </span>
              </span>
            </div>
          </div>
        </Fly>
      </div>
      <TabBar
        d={1.3}
        tabs={[
          { icon: faTableCellsLarge, label: "Dashboard", isActive: true },
          { icon: faBriefcase, label: "Jobs", badge: "2" },
          { icon: faWallet, label: "Wallet" },
          { icon: faMessages, label: "Messages" },
          { icon: faUser, label: "Profile" },
        ]}
      />
    </div>
  </PhoneFrame>
);

/* ---------- member dashboard ---------- */

const MemberPhone = () => (
  <PhoneFrame className="left-0 top-[20%] z-10 w-[282px] sm:w-[302px]" d={0.05} r={-4}>
    <div className="h-full">
      <StatusBar />
      <PhoneHeader
        initials="QS"
        name="Quincy Starr"
        d={0.56}
        rightIcons={
          <>
            <FontAwesomeIcon icon={faBell} className="h-3.5 w-3.5" />
            <FontAwesomeIcon icon={faCalendarCirclePlus} className="h-3.5 w-3.5" />
          </>
        }
      />
      <div className="px-5 pt-4">
        <Fly d={0.68}>
          <Label>Your Content</Label>
          <span className="mt-1 flex items-baseline gap-2">
            <span className="text-[27px] font-bold leading-none text-white">75</span>
            <span className="text-[13px] text-zinc-300">photos &amp; videos</span>
          </span>
          <span className="mt-1.5 flex items-center gap-1 text-[10px] font-bold" style={{ color: LIME }}>
            <FontAwesomeIcon icon={faArrowTrendUp} className="h-2.5 w-2.5" />
            +5 past 90 days
          </span>
        </Fly>
        <Fly d={0.8} className="mt-3 flex gap-1.5 overflow-hidden">
          {["001", "002", "003", "004"].map((n) => (
            <img
              key={n}
              src={`/film-thumbs/${n}.webp`}
              alt=""
              width={56}
              height={56}
              loading="lazy"
              decoding="async"
              className="h-14 w-14 shrink-0 rounded-lg object-cover"
            />
          ))}
        </Fly>
        <Fly d={0.92} className="mt-3 flex gap-2">
          <LimePill>View Content</LimePill>
          <OutlinePill hasArrow={false}>New Reservation</OutlinePill>
        </Fly>
        <Fly d={1.04} className="mt-4">
          <div className="rounded-2xl bg-[#101010] p-3.5">
            <div className="flex items-center justify-between">
              <Label>Hours Bank</Label>
              <Label>36% of plan</Label>
            </div>
            <span className="mt-1 flex items-baseline gap-1.5">
              <span className="text-[22px] font-bold leading-none text-white">285/800</span>
              <span className="text-[10px] text-zinc-400">hours</span>
            </span>
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-zinc-800">
              <div className="h-full w-[36%] rounded-full" style={{ backgroundColor: CYAN }} />
            </div>
            <div className="mt-1 flex justify-between text-[8px] text-zinc-500">
              <span>285 used</span>
              <span>515 remaining</span>
            </div>
          </div>
        </Fly>
        <div className="mt-3 flex gap-2">
          <Fly d={1.16} className="min-w-0 flex-1">
            <div className="rounded-xl bg-[#101010] p-3">
              <Label>Next Reservation</Label>
              <span className="mt-1 block text-[17px] font-bold text-white">Today</span>
              <span
                className="mt-1 inline-block rounded-full px-2 py-0.5 text-[8px] font-bold"
                style={{ color: "#F5B843", backgroundColor: "rgba(245,184,67,0.12)" }}
              >
                Pending
              </span>
            </div>
          </Fly>
          <Fly d={1.24} className="min-w-0 flex-1">
            <div className="rounded-xl bg-[#101010] p-3">
              <Label>Upcoming</Label>
              <span className="mt-1 block text-[17px] font-bold text-white">15</span>
              <span className="mt-0.5 block text-[8px] text-zinc-500">reservations this month</span>
            </div>
          </Fly>
        </div>
      </div>
      <TabBar
        d={1.36}
        tabs={[
          { icon: faTableCellsLarge, label: "Dashboard", isActive: true },
          { icon: faCalendarDays, label: "Reservations" },
          { icon: faImages, label: "Content" },
          { icon: faClock, label: "Hours" },
          { icon: faEllipsis, label: "More" },
        ]}
      />
    </div>
  </PhoneFrame>
);

/* ---------- glass notification chips ---------- */

const CHIPS = [
  {
    dot: LIME,
    title: "Payout cleared",
    detail: "$340 · Westside Tournament",
    check: false,
    className: "left-[2%] top-[2%] sm:left-[6%]",
    d: 1.5,
    fx: -60,
  },
  {
    dot: CYAN,
    title: "Upload due",
    detail: "Falcons BBall · 24h left",
    check: false,
    className: "right-[0%] top-[44%] hidden sm:flex",
    d: 1.66,
    fx: 60,
  },
  {
    dot: LIME,
    title: "Applied",
    detail: "We'll confirm within 24h",
    check: true,
    className: "left-[0%] bottom-[8%] hidden md:flex",
    d: 1.82,
    fx: -60,
  },
] as const;

const GlassChip = ({ chip }: { chip: (typeof CHIPS)[number] }) => (
  <Fly
    d={chip.d}
    fx={chip.fx}
    fy={30}
    className={`absolute z-20 ${chip.className.includes("hidden") ? "" : ""} ${chip.className}`}
  >
    <div className="gta-chip-drift flex items-center gap-3 rounded-[20px] border border-white/10 bg-[#0E0E0E]/85 px-4 py-3 shadow-[0_12px_48px_rgba(0,0,0,0.55)] backdrop-blur-xl">
      <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: chip.dot }} />
      <span className="whitespace-nowrap">
        <span className="flex items-center gap-1.5 text-[14px] font-bold leading-tight text-white">
          {chip.title}
          {chip.check && <FontAwesomeIcon icon={faCheck} className="h-3 w-3" />}
        </span>
        <span className="block text-[12px] leading-tight text-zinc-400">{chip.detail}</span>
      </span>
    </div>
  </Fly>
);

/* ---------- showcase ---------- */

const GetTheAppDevices = () => {
  const { ref, inView } = useInViewOnce();
  return (
    <div
      ref={ref}
      className={`gta-devices relative mx-auto h-[460px] w-full max-w-[560px] overflow-hidden sm:h-[510px] lg:h-auto lg:min-h-[640px] lg:max-w-none lg:overflow-visible ${inView ? "gta-in" : ""}`}
      aria-hidden="true"
    >
      <style>{CSS}</style>
      <CreatorPhone />
      <MemberPhone />
      {CHIPS.map((c) => (
        <GlassChip key={c.title} chip={c} />
      ))}
    </div>
  );
};

export default GetTheAppDevices;
